import { Edge, Node } from '@xyflow/react';
import { AgentEventTypes, type AgentEventLog } from '../../../api/events-client/events-client.type';
import type { TaskTransition } from '../../tasks-table/tasks-table.type';
import { AgentNode, type SessionMetadata, type TaskStatusPeriod } from '../agentic-process.type';

const COLUMN_Y_SPACING = 50;
const COLLAPSED_SESSION_SPACING = 125; // Spacing between collapsed sessions
const SELF_EDGE_SPACING = 75;
const SESSION_X_POSITION = -75;
const TASK_STATUS_X_POSITION = -375;

const COLUMN_X_POSITIONS = {
  [AgentNode.USER]: 94,
  [AgentNode.AGENT]: 394,
  [AgentNode.TOOLS]: 694,
};

/**
 * Calculate chronological status periods from task transitions
 * @param {TaskTransition[]} taskTransitions - The task transitions to analyze
 * @param {Record<string, SessionMetadata>} sessionMetadata - min and max session start and end times
 * @returns {TaskStatusPeriod[]} Array of status periods with start/end times
 */
const calculateTaskStatusPeriods = (
  taskTransitions: TaskTransition[],
  sessionMetadata: Record<string, SessionMetadata>,
): TaskStatusPeriod[] => {
  if (!taskTransitions?.length) return [];
  const minSessionStartTime = Math.min(...Object.values(sessionMetadata).map((s) => new Date(s.startDate).getTime()));
  const maxSessionEndTime = Math.max(...Object.values(sessionMetadata).map((s) => new Date(s.endDate).getTime()));

  // Sort transitions by changed_at timestamp (oldest first)
  const sortedTransitions = [...taskTransitions].sort(
    (a, b) => new Date(a.changed_at).getTime() - new Date(b.changed_at).getTime(),
  );

  const periods: TaskStatusPeriod[] = [];

  for (let i = 0; i < sortedTransitions.length; i++) {
    const currentTransition = sortedTransitions[i];
    const nextTransition = sortedTransitions[i + 1];
    const isCurrentStatus = !nextTransition; // If no next transition, this is current status

    // Only push periods if they overlap with the min and max session end datetime,
    // treat null start time as negative infinity and null end time as positive infinity
    const periodStart = currentTransition.changed_at ? new Date(currentTransition.changed_at).getTime() : -Infinity;
    const periodEnd = nextTransition?.changed_at ? new Date(nextTransition.changed_at).getTime() : Infinity;

    // Overlap if periodEnd > minSessionStartTime and periodStart < maxSessionEndTime
    if (periodEnd > minSessionStartTime && periodStart < maxSessionEndTime) {
      periods.push({
        status: currentTransition.status,
        startTime: currentTransition.changed_at,
        endTime: nextTransition?.changed_at || null,
        transition: currentTransition,
        isCurrentStatus,
      });
    }
  }

  return periods;
};

/**
 * Calculate session statistics
 * @param {AgentEventLog[]} agentEvents - The agent events to calculate statistics for
 * @returns {Record<string, { userPromptCount: number; toolCallCount: number }>} - A record of session IDs with their statistics
 */
const calculateSessionStatistics = (
  agentEvents: AgentEventLog[],
): Record<string, { userPromptCount: number; toolCallCount: number }> => {
  const sessionStats: Record<string, { userPromptCount: number; toolCallCount: number }> = {};

  agentEvents.forEach((event) => {
    if (!event.session_id) return;

    if (!sessionStats[event.session_id]) {
      sessionStats[event.session_id] = {
        userPromptCount: 0,
        toolCallCount: 0,
      };
    }

    if (event.event_type === AgentEventTypes.USER_PROMPT) {
      sessionStats[event.session_id].userPromptCount++;
    } else if (event.event_type === AgentEventTypes.TOOL_CALL) {
      sessionStats[event.session_id].toolCallCount++;
    }
  });

  return sessionStats;
};

/**
 * Sort agent events by session_id and datetime
 * @param {AgentEventLog[]} agentEvents - The agent events to sort
 * @returns {AgentEventLog[]} - The sorted agent events
 */
const sortAgentEvents = (agentEvents: AgentEventLog[]): AgentEventLog[] => {
  // Group events by session_id only
  const groupedEvents: Record<string, AgentEventLog[]> = {};

  agentEvents.forEach((event) => {
    // Only group by session_id, skip events without session_id
    if (!event.session_id) return;

    const key = event.session_id;
    if (!groupedEvents[key]) {
      groupedEvents[key] = [];
    }
    groupedEvents[key].push(event);
  });

  // Sort each session group first by datetime, then by correlation vector
  Object.keys(groupedEvents).forEach((sessionId) => {
    groupedEvents[sessionId].sort((a, b) => {
      // First, sort by datetime
      const aTime = new Date(a.event_date).getTime();
      const bTime = new Date(b.event_date).getTime();

      if (aTime !== bTime) {
        return aTime - bTime;
      }

      // If datetime is the same, sort by correlation vector
      if (a.correlation_vector && b.correlation_vector) {
        // Split by dots and compare each segment numerically if possible
        const aParts = a.correlation_vector.split('.');
        const bParts = b.correlation_vector.split('.');

        // Compare each segment
        for (let i = 0; i < Math.min(aParts.length, bParts.length); i++) {
          // If both parts are numeric, compare as numbers
          const aNum = parseInt(aParts[i], 10);
          const bNum = parseInt(bParts[i], 10);

          if (!isNaN(aNum) && !isNaN(bNum)) {
            if (aNum !== bNum) {
              return aNum - bNum;
            }
          } else {
            // If not numeric, compare as strings
            const stringCompare = aParts[i].localeCompare(bParts[i]);
            if (stringCompare !== 0) {
              return stringCompare;
            }
          }
        }

        // If one CV is longer than the other but all compared parts are equal
        return aParts.length - bParts.length;
      }

      // Handle missing correlation vectors
      if (!a.correlation_vector) return 1;
      if (!b.correlation_vector) return -1;

      return 0; // Both have same datetime and no correlation vectors to compare
    });
  });

  // Sort sessions by their earliest event date and flatten the result
  return Object.keys(groupedEvents)
    .sort((a, b) => {
      const aFirstDate = new Date(groupedEvents[a][0]?.event_date || 0).getTime();
      const bFirstDate = new Date(groupedEvents[b][0]?.event_date || 0).getTime();
      return aFirstDate - bFirstDate;
    })
    .flatMap((sessionId) => groupedEvents[sessionId]);
};

/**
 * Find the to and from nodes for the event
 * @param {AgentEventLog} event - The agent event to get the agent node enum for
 * @returns {Object} Object containing:
 *   - to: The agent node to which the event is connected
 *   - from: The agent node from which the event is connected
 */
const getAgentNodesForEvent = (
  event: AgentEventLog,
): {
  to: AgentNode;
  from: AgentNode;
  inverse?: boolean;
  connectToSelf?: boolean;
} => {
  switch (event.event_type) {
    case AgentEventTypes.USER_PROMPT:
    case AgentEventTypes.HUMAN_INPUT_RECEIVED:
    case AgentEventTypes.AGENT_CALL:
      return {
        from: AgentNode.USER,
        to: AgentNode.AGENT,
      };
    case AgentEventTypes.AGENT_RESULT:
    case AgentEventTypes.AGENT_ERROR:
    case AgentEventTypes.HUMAN_INPUT_REQUESTED:
      return {
        from: AgentNode.AGENT,
        to: AgentNode.USER,
        inverse: true,
      };
    case AgentEventTypes.LLM_START:
    case AgentEventTypes.LLM_END:
    case AgentEventTypes.THINKING_START:
    case AgentEventTypes.THINKING_END:
    case AgentEventTypes.PLANNING_START:
    case AgentEventTypes.PLANNING_END:
      return {
        from: AgentNode.AGENT,
        to: AgentNode.AGENT,
        connectToSelf: true,
      };
    case AgentEventTypes.TOOL_CALL:
      return {
        from: AgentNode.AGENT,
        to: AgentNode.TOOLS,
      };
    case AgentEventTypes.TOOL_RESULT:
    case AgentEventTypes.TOOL_ERROR:
      return {
        from: AgentNode.TOOLS,
        to: AgentNode.AGENT,
        inverse: true,
      };
    default:
      return {
        from: AgentNode.AGENT,
        to: AgentNode.USER,
        inverse: true,
      };
  }
};

/**
 * Create nodes and edges for task status periods
 * @param {TaskStatusPeriod[]} statusPeriods - The status periods to create nodes for
 * @param {Record<string, SessionMetadata>} sessionMetadata - Session metadata for Y positioning
 * @returns {Object} Object containing nodes and edges for task status changes
 */
const createTaskStatusNodesAndEdges = (
  statusPeriods: TaskStatusPeriod[],
  sessionMetadata: Record<string, SessionMetadata>,
): { nodes: Node[]; edges: Edge[] } => {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  if (!statusPeriods?.length) return { nodes, edges };

  // Get the overall date range from all sessions to calculate Y positions
  const allSessions = Object.values(sessionMetadata);
  if (!allSessions.length) return { nodes, edges };

  // Find the earliest and latest times across all sessions
  const earliestSessionStart = Math.min(...allSessions.map((s) => new Date(s.startDate).getTime()));
  const latestSessionEnd = Math.max(...allSessions.map((s) => new Date(s.endDate).getTime()));
  const overallTimeRange = latestSessionEnd - earliestSessionStart;

  // Use the Y bounds from the session metadata (stay within existing session bounds)
  const minY = Math.min(...allSessions.map((s) => s.startY));
  const maxY = Math.max(...allSessions.map((s) => s.endY));
  const totalYRange = maxY - minY;

  statusPeriods.forEach((period, index) => {
    const periodStartTime = new Date(period.startTime);

    // Calculate start Y position based on timeline
    const startOffset = Math.max(0, periodStartTime.getTime() - earliestSessionStart);
    const startRatio = overallTimeRange > 0 ? Math.min(1, startOffset / overallTimeRange) : 0;
    const startYPosition = minY + startRatio * totalYRange;

    // For end position: if current status (no end time), use maxY (lowest Y position)
    // Otherwise calculate based on end time
    let endYPosition: number;
    if (!period.endTime) {
      // Current status - draw end node at the lowest possible Y (maxY)
      endYPosition = maxY;
    } else {
      // Has end time - calculate Y position based on timeline
      const periodEndTime = new Date(period.endTime);
      const endOffset = Math.max(0, periodEndTime.getTime() - earliestSessionStart);
      const endRatio = overallTimeRange > 0 ? Math.min(1, endOffset / overallTimeRange) : 0;
      endYPosition = minY + endRatio * totalYRange;
    }

    // Create start and end nodes for the status period
    const startNodeId = `task-status-${index}-start`;
    const endNodeId = `task-status-${index}-end`;

    const startNode = {
      id: startNodeId,
      type: 'dot',
      position: { x: TASK_STATUS_X_POSITION, y: startYPosition },
      data: {
        vertical: true,
      },
    };

    const endNode = {
      id: endNodeId,
      type: 'dot',
      position: { x: TASK_STATUS_X_POSITION, y: endYPosition },
      data: {
        vertical: true,
      },
    };

    nodes.push(startNode, endNode);

    edges.push({
      id: `task-status-${index}-edge`,
      source: startNodeId,
      target: endNodeId,
      type: 'taskStatus',
      data: {
        taskTransition: period.transition,
        isCurrentStatus: period.isCurrentStatus,
        statusName: period.status,
        startTime: period.startTime,
        endTime: period.endTime,
      },
    });
  });

  return { nodes, edges };
};

/**
 * Create nodes and edges for an event
 * @param {AgentEventLog} event - The agent event to create nodes and edges for
 * @param {number} currentY - The current Y position
 * @param {Function} setSelectedEvent - The function to set the selected event
 * @param {AgentEventLog | null} selectedEvent - The currently selected event
 * @returns {Object} Object containing:
 *   - nodes: The nodes for the event
 *   - edges: The edges for the event
 *   - currentY: The new Y position after adding the event
 */
const createNodesAndEdgesForEvent = (
  event: AgentEventLog,
  currentY: number,
  setSelectedEvent: (event: AgentEventLog) => void,
  selectedEvent?: AgentEventLog | null,
): { nodes: Node[]; edges: Edge[]; currentY: number } => {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  const { to, from, inverse, connectToSelf } = getAgentNodesForEvent(event);

  const fromNode = {
    id: `${event.id}-from`,
    type: 'dot',
    position: { x: COLUMN_X_POSITIONS[from], y: currentY },
    data: { label: event.event_type, event, inverse, connectToSelf, isFrom: true },
  };
  const toNodeY = connectToSelf ? currentY + SELF_EDGE_SPACING : currentY;
  const toNode = {
    id: `${event.id}-to`,
    type: 'dot',
    position: { x: COLUMN_X_POSITIONS[to], y: toNodeY },
    data: { label: event.event_type, event, inverse, connectToSelf, isFrom: false },
  };

  nodes.push(fromNode, toNode);

  edges.push({
    id: `${event.id}-edge`,
    source: `${event.id}-from`,
    target: `${event.id}-to`,
    type: 'agentEvent',
    data: {
      event: event,
      connectToSelf,
      setSelectedEvent,
      selectedEvent,
    },
  });

  return { nodes, edges, currentY: toNodeY };
};

/**
 * Get the nodes and edges for the agent graph with 3-column layout
 * Each action/log becomes its own node positioned by correlation vector
 * @param {AgentEventLog[]} agentEvents - The agent events to get the nodes and edges for
 * @param {Set<string>} collapsedSessions - The sessions that are collapsed
 * @param {TaskTransition[]} taskTransitions - The task transitions to display
 * @returns {Object} Object containing:
 *   - nodes: Individual event nodes positioned in 3 columns with header nodes
 *   - edges: Edges connecting events between columns
 */
const getAgentGraphNodes = (
  agentEvents: AgentEventLog[],
  collapsedSessions: Set<string>,
  toggleSessionCollapse: (sessionId: string) => void,
  setSelectedEvent: (event: AgentEventLog) => void,
  taskTransitions?: TaskTransition[],
  showTaskStatus?: boolean,
  selectedEvent?: AgentEventLog | null,
): { nodes: Node[]; edges: Edge[] } => {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  // Always add header nodes at the top
  nodes.push(
    {
      id: 'header-user',
      type: 'header',
      position: { x: 0, y: 0 },
      data: { label: 'User' },
    },
    {
      id: 'header-agent',
      type: 'header',
      position: { x: 300, y: 0 },
      data: { label: 'Agent' },
    },
    {
      id: 'header-tools',
      type: 'header',
      position: { x: 600, y: 0 },
      data: { label: 'Tools' },
    },
  );

  if (!agentEvents?.length) {
    return { nodes, edges };
  }

  const sortedEvents = sortAgentEvents(agentEvents);
  const sessionStatistics = calculateSessionStatistics(agentEvents);

  let currentY = 100;
  let sessionMetadata: Record<string, SessionMetadata> = {};
  let processedSessions = new Set<string>();

  sortedEvents.forEach((event) => {
    const isSessionCollapsed = event.session_id && collapsedSessions.has(event.session_id);
    const renderEvent = event.session_id && !isSessionCollapsed;
    const isFirstEventInSession = event.session_id && !processedSessions.has(event.session_id);

    if (renderEvent) {
      const {
        nodes: eventNodes,
        edges: eventEdges,
        currentY: newCurrentY,
      } = createNodesAndEdgesForEvent(event, currentY, setSelectedEvent, selectedEvent);
      currentY = newCurrentY;
      nodes.push(...eventNodes);
      edges.push(...eventEdges);
    }

    if (event.session_id && !sessionMetadata[event.session_id]) {
      const stats = sessionStatistics[event.session_id] || { userPromptCount: 0, toolCallCount: 0 };
      sessionMetadata[event.session_id] = {
        sessionId: event.session_id,
        isCollapsed: !!isSessionCollapsed,
        agentSource: event.agent_source,
        startDate: event.event_date,
        endDate: event.event_date,
        startY: currentY,
        endY: currentY,
        userPromptCount: stats.userPromptCount,
        toolCallCount: stats.toolCallCount,
      };
      processedSessions.add(event.session_id);
    } else if (event.session_id && sessionMetadata[event.session_id]) {
      sessionMetadata[event.session_id].endDate = event.event_date;
      sessionMetadata[event.session_id].endY = currentY;
    }

    // Advance Y position for proper spacing
    if (renderEvent) {
      currentY += COLUMN_Y_SPACING;
    } else if (isFirstEventInSession && isSessionCollapsed) {
      // First event of a collapsed session - add collapsed session spacing
      currentY += COLLAPSED_SESSION_SPACING;
      if (sessionMetadata[event.session_id]) {
        sessionMetadata[event.session_id].endY = currentY - 20; // Small offset for visual separation
      }
    }
  });

  const { nodes: sessionNodes, edges: sessionEdges } = getAgentGraphSessionNodes(
    sessionMetadata,
    toggleSessionCollapse,
  );
  nodes.push(...sessionNodes);
  edges.push(...sessionEdges);

  // Add task status nodes and edges if task transitions are provided
  if (taskTransitions?.length && showTaskStatus) {
    // Calculate chronological status periods
    const statusPeriods = calculateTaskStatusPeriods(taskTransitions, sessionMetadata);

    // Always render task status nodes (don't filter by session overlap)
    if (statusPeriods.length) {
      const { nodes: taskStatusNodes, edges: taskStatusEdges } = createTaskStatusNodesAndEdges(
        statusPeriods,
        sessionMetadata,
      );
      nodes.push(...taskStatusNodes);
      edges.push(...taskStatusEdges);
    }
  }

  // Add footer nodes and edges at the bottom
  nodes.push(
    {
      id: 'footer-user',
      type: 'dot',
      position: { x: COLUMN_X_POSITIONS[AgentNode.USER], y: currentY },
      data: { label: 'User', vertical: true },
    },
    {
      id: 'footer-agent',
      type: 'dot',
      position: { x: COLUMN_X_POSITIONS[AgentNode.AGENT], y: currentY },
      data: { label: 'Agent', vertical: true },
    },
    {
      id: 'footer-tools',
      type: 'dot',
      position: { x: COLUMN_X_POSITIONS[AgentNode.TOOLS], y: currentY },
      data: { label: 'Tools', vertical: true },
    },
  );

  const edgeStyle = {
    stroke: '#000000',
    strokeWidth: 4,
  };

  edges.push(
    {
      id: 'edge-user-header-to-footer',
      source: 'header-user',
      target: 'footer-user',
      type: 'straight',
      style: edgeStyle,
    },
    {
      id: 'edge-agent-header-to-footer',
      source: 'header-agent',
      target: 'footer-agent',
      type: 'straight',
      style: edgeStyle,
    },
    {
      id: 'edge-tools-header-to-footer',
      source: 'header-tools',
      target: 'footer-tools',
      type: 'straight',
      style: edgeStyle,
    },
  );
  return { nodes, edges };
};

/**
 * Get the nodes and edges for the agent graph session nodes
 * @param {Record<string, SessionMetadata>} sessionMetadata - The session metadata
 * @param {Function} toggleSessionCollapse - The function to toggle the session collapse
 * @returns {Object} Object containing:
 *   - nodes: The session nodes
 *   - edges: The session edges
 */
const getAgentGraphSessionNodes = (
  sessionMetadata: Record<string, SessionMetadata>,
  toggleSessionCollapse?: (sessionId: string) => void,
): { nodes: Node[]; edges: Edge[] } => {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  Object.values(sessionMetadata).forEach((session) => {
    nodes.push({
      id: `session-${session.sessionId}-start`,
      type: 'dot',
      position: { x: SESSION_X_POSITION, y: session.startY },
      data: { label: session.agentSource, session, vertical: true },
    });

    nodes.push({
      id: `session-${session.sessionId}-end`,
      type: 'dot',
      position: { x: SESSION_X_POSITION, y: session.endY },
      data: { label: session.agentSource, session, vertical: true },
    });

    edges.push({
      id: `session-${session.sessionId}-edge`,
      source: `session-${session.sessionId}-start`,
      target: `session-${session.sessionId}-end`,
      type: 'session',
      data: { session, toggleSessionCollapse },
    });
  });

  return { nodes, edges };
};

export { getAgentGraphNodes };
